import { DecimalSchema } from "../../decimal/decimal.schema";
import { QuestRewardDataSchema } from "./quest-cms-interaction";
import { QuestProgressMetadataSchema } from "./quest-progress";
import { QUEST_FREQUENCY, QUEST_REWARD_TYPE, QUEST_TYPE } from "./quest.enums";
import { z } from "zod";

export const QuestRewardSchema = z.object({
  type: z.nativeEnum(QUEST_REWARD_TYPE),
  count: z.number().int().positive(),
});

export type QuestReward = z.infer<typeof QuestRewardSchema>;

export const QuestCompletionSchema = z
  .object({
    id: z.uuid(),
    hubUserId: z.number(),
    questId: z.number(),
    questName: z.string().min(1),
    questType: z.nativeEnum(QUEST_TYPE),
    questFrequency: z.nativeEnum(QUEST_FREQUENCY),
    rewardType: z.nativeEnum(QUEST_REWARD_TYPE),
    rewardCount: DecimalSchema,
    // Snapshot of the quest's payout spec at completion time — a later CMS edit must not
    // change what an already-earned completion pays out.
    rewardData: QuestRewardDataSchema.optional(),
    rewardClaimed: z.boolean().default(false),
    rewardClaimedAt: z.coerce.date().nullable().optional(),
    completedAt: z.coerce.date(),
    completionMetadata: QuestProgressMetadataSchema,
    periodStart: z.coerce.date(),
    periodEnd: z.coerce.date(),
    weekNumber: z.number().positive(),
    dayOfYear: z.number().positive(),
    monthNumber: z.number().min(1).max(12),
    year: z.number().positive(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
  })
  .superRefine((data, ctx) => {
    if (data.rewardClaimed && !data.rewardClaimedAt) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "rewardClaimedAt is required when rewardClaimed is true",
        path: ["rewardClaimedAt"],
      });
    }
  });

export type QuestCompletion = z.infer<typeof QuestCompletionSchema>;

export const QuestEligibilityItemSchema = z.object({
  questId: z.number().positive(),
  canComplete: z.boolean(),
  reason: z.string().optional(),
  nextEligibleDate: z.coerce.date().optional(),
  lastCompletionDate: z.coerce.date().optional(),
  // End of the user's current attempt window. Present before any progress exists, which is
  // what lets a signup-anchored quest render a countdown to a user who hasn't started it.
  windowEndsAt: z.coerce.date().optional(),
});

export type QuestEligibilityItem = z.infer<typeof QuestEligibilityItemSchema>;
